import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { forwardRef, useImperativeHandle } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import "./RegistrationForm.css";
import { v4 as uuidv4 } from "uuid";
import { useFormContext } from "./RegistrationFormContext";
import moment from "moment-hijri";
import DropdownWithCustomValuesModal from "../../components/DropdownWithCustomValuesModal";
import StepSuccessModal from "./StepSuccessModal";
import RegStepItemDeleteInfoModal from "./RegStepItemDeleteInfoModal";

const Step2Pupils = forwardRef(({ isOpen, moveToNextStep }, ref) => {
  const token = localStorage.getItem("token");
  const roleId = localStorage.getItem("roleId");
  const organizationId = localStorage.getItem("organizationId");
  const centerId = localStorage.getItem("centerId");

  const { sharedData } = useFormContext();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const {
    register,
    watch,
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      admissionInquiryDtos: [],
      currentPupil: {
        id: 0,
        familyCode: sharedData.code || "",
        centerSessionId: "",
        termId: "",
        isAttended: "",
        firstName: "",
        middleName: "",
        lastName: "",
        arabicForeNames: "",
        arabicMiddleName: "",
        arabicSurName: "",
        greGorianDOB: "",
        hijriDOB: "",
        isSibilingEnrolled: "",
        nationality: "",
        gender: "",
        iqamaNumber: "",
        iqamaExpiryDate: null,
        passportCountryId: null,
        passportExpiryDate: null,
        passportNumber: null,
        passportValidFrom: "",
        gccNumber: "",
        gccExpiryDate: null,
        saudiId: "",
        fluentInEnglish: "",
        fluentInArabic: "",
        homeLanguageId: "",
        createdBy: localStorage.getItem("userId"),
        updatedBy: localStorage.getItem("userId"),
        userId: sharedData.userId,
        organizationId: organizationId,
        centerId: centerId,
        code: "",
        isSubmit: false,
        isCenterSubmit: false,
        centerUpdatedBy: "",
        assessmentSlotId: 0,
      },
    },
  });

  const [lastAddedIndex, setLastAddedIndex] = useState(null);
  const [editingPupilIndex, setEditingPupilIndex] = useState(null);

  const [termsData, setTermsData] = useState([]);
  const [sessionsData, setSessionsData] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [nationalityData, setNationalityData] = useState([]);
  const [homeLanguageData, setHomeLanguageData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [customDropdownName, setCustomDropdownName] = useState("");
  const [
    isCustomDropdownValuesModalVisible,
    setIsCustomDropdownValuesModalVisible,
  ] = useState(false);

  const gregorianDOB = watch("currentPupil.greGorianDOB");
  const nationality = watch("currentPupil.nationality");

  useEffect(() => {
    if (gregorianDOB) {
      const hijri = moment(gregorianDOB, "YYYY-MM-DD").format("iDD/iMM/iYYYY");
      setValue("currentPupil.hijriDOB", hijri);
    }
  }, [gregorianDOB, setValue]);

  const fetchTermsByOrgId = async () => {
    try {
      const response = await get(
        `StudentEvaluation/GetAllEvaluationCriteriaType?OrganizationId=${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setTermsData(response?.evaluationTypes);
    } catch (error) {
      console.error("Error fetching terms:", error);
    }
  };

  useEffect(() => {
    fetchTermsByOrgId();
  }, []);

  const getSessionsList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `Session/GetSessionByCenterId?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSessionsData(response.session);
    } catch (error) {
      console.error("Error fetching sessions:", error);
    }
  };

  const fetchClassesByCenterId = async () => {
    try {
      const response = await get(
        `CenterClass/GetClassCenterByCenterId?id=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setClassesData(response);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  useEffect(() => {
    getSessionsList();
    fetchClassesByCenterId();
  }, []);

  useEffect(() => {
    if (sessionsData.length > 0) {
      setValue("currentPupil.centerSessionId", sessionsData[0].id);
    }
  }, [sessionsData, setValue]);

  // useEffect(() => {
  //   fetchCommonSettings("nationality", setNationalityData);
  //   fetchCommonSettings("homeLanguage", setHomeLanguageData);
  // }, [token]);

  useEffect(() => {
    if (nationalityData.length > 0) {
      const saudiOption = nationalityData.find(
        (option) =>
          option.value?.toLowerCase() === "saudi" ||
          option.name?.toLowerCase() === "saudi"
      );
      setValue(
        "currentPupil.nationality",
        saudiOption
          ? saudiOption.value.toString()
          : nationalityData[0].value.toString(),
        { shouldValidate: true }
      );
    }
  }, [nationalityData, setValue]);

  useEffect(() => {}, [token]);

  // Pupils Field Array
  const {
    fields: admissionInquiryDtos,
    append: appendPupil,
    remove: removePupil,
    update: updatePupil,
  } = useFieldArray({
    control,
    name: "admissionInquiryDtos",
    keyName: "rhfId",
  });

  const getPupilsList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    if (!sharedData.userId) {
      console.error("No valid userId available");
      return;
    }

    setIsLoading(true);

    try {
      const response = await get(
        `Student/GetPupilsInformationByUserId?userId=${sharedData.userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response && response.length > 0) {
        const mappedPupils = response.map((pupil) => ({
          pupilId: pupil.id,
          rhfId: pupil.id,
          familyCode: pupil.familyCode || sharedData.code || "",
          centerSessionId: pupil.centerSessionId || "",
          termId: pupil.termId || "",
          isAttended: pupil.isAttended ? "Yes" : "No" || "",
          firstName: pupil.firstName || "",
          middleName: pupil.middleName || "",
          lastName: pupil.lastName || "",
          arabicForeNames: pupil.arabicForeNames || "",
          arabicMiddleName: pupil.arabicMiddleName || "",
          arabicSurName: pupil.arabicSurName || "",
          greGorianDOB: pupil.greGorianDOB?.split("T")[0] || "",
          hijriDOB: pupil.hijriDOB || "",
          isSibilingEnrolled: pupil.isSibilingEnrolled ? "Yes" : "No" || "",
          nationality: pupil.nationality || "",
          gender: pupil.gender,
          iqamaNumber: pupil.iqamaNumber || "",
          iqamaExpiryDate: pupil.iqamaExpiryDate?.split("T")[0] || null,
          passportCountryId: pupil.passportCountryId || null,
          passportExpiryDate: pupil.passportExpiryDate?.split("T")[0] || null,
          passportNumber: pupil.passportNumber || null,
          passportValidFrom: pupil.passportValidFrom?.split("T")[0] || "",
          gccNumber: pupil.gccNumber || "",
          gccExpiryDate: pupil.gccExpiryDate?.split("T")[0] || null,
          saudiId: pupil.saudiId || "",
          fluentInEnglish: pupil.fluentInEnglish || "",
          fluentInArabic: pupil.fluentInArabic || "",
          homeLanguageId: pupil.homeLanguageId || "",
          createdBy: pupil.createdBy || localStorage.getItem("userId"),
          updatedBy: pupil.updatedBy || localStorage.getItem("userId"),
          userId: sharedData.userId,
          organizationId: organizationId,
          centerId: centerId,
          code: pupil.code || "",
          isSubmit: pupil.isSubmit,
          isCenterSubmit: pupil.isCenterSubmit,
          centerSubmissionDate: pupil.centerSubmissionDate,
          centerUpdatedBy: pupil.centerUpdatedBy,
          assessmentSlotId: pupil.assessmentSlotId,

          assessmentDate: pupil.assessmentDate,
          communicationRemarks: pupil.communicationRemarks || "",
          isCommunicatedToGuardian: pupil.isCommunicatedToGuardian,
          centerClassId: pupil.centerClassId,
          isInterviewScheduled: pupil.isInterviewScheduled,
          isInterviewTaken: pupil.isInterviewTaken,
          interviewRemarks: pupil.interviewRemarks || "",
          interviewDate: pupil.interviewDate,
          isAssessmentAdded: pupil.isAssessmentAdded,
          isAssessmentMarksAdded: pupil.isAssessmentMarksAdded,
          isAccepted: pupil.isAccepted,
          acceptanceRemarks: pupil.acceptanceRemarks || "",
          hasObservation: pupil.hasObservation,
          observationRemarks: pupil.observationRemarks || "",
          shouldRetakeExam: pupil.shouldRetakeExam,
          retakeExamRemarks: pupil.retakeExamRemarks || "",
          registrationVoucherNo: pupil.registrationVoucherNo,
          isPaidRegistration: pupil.isPaidRegistration,
          admissionVoucherNo: pupil.admissionVoucherNo,
          isPaidAdmission: pupil.isPaidAdmission,
        }));

        setValue("admissionInquiryDtos", mappedPupils);
      }
    } catch (error) {
      console.error("Error fetching pupils:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && sharedData.userId) {
      getPupilsList();
      fetchCommonSettings("nationality", setNationalityData);
      fetchCommonSettings("homeLanguage", setHomeLanguageData);
    }
  }, [isOpen, sharedData.userId]);

  // Dropdowns data start

  const fetchCommonSettings = async (moduleName, setData) => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${moduleName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setData(response?.commonSettingDetail);
    } catch (error) {
      console.error(`Error fetching ${moduleName}:`, error);
    }
  };

  // useEffect(() => {
  //   fetchCommonSettings("relationShip", setRelationshipData);
  // }, [token]);

  // Dropdowns data end

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const selectedCountry = watch("currentContact.countryId");
  const selectedState = watch("currentContact.stateId");

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await get("Dropdown/GetCountries", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setCountries(response);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCountries();
  }, [token]);

  // Fetch states when a country is selected
  useEffect(() => {
    const fetchStates = async () => {
      if (selectedCountry) {
        try {
          const response = await get(
            `Dropdown/GetStates?countryId=${selectedCountry}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setStates(response);

          // Preserve existing state if editing
          const currentState = getValues("currentContact.stateId");
          if (!currentState) {
            setValue("currentContact.stateId", null);
            setValue("currentContact.cityId", null);
          }
        } catch (error) {
          console.error("Error fetching states:", error);
        }
      }
    };

    fetchStates();
  }, [selectedCountry]);

  // Fetch cities when a state is selected
  useEffect(() => {
    const fetchCities = async () => {
      if (selectedState) {
        try {
          const response = await get(
            `Dropdown/GetCities?stateId=${selectedState}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setCities(response);

          // Preserve existing city if editing
          const currentCity = getValues("currentContact.cityId");
          if (!currentCity) {
            setValue("currentContact.cityId", null);
          }
        } catch (error) {
          console.error("Error fetching cities:", error);
        }
      }
    };

    fetchCities();
  }, [selectedState]);

  // API call for saving admissionInquiryDtos
  const savePupils = async (payload) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await post("Student/UpdateAdmissionInquery", payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.isSuccess) {
        getPupilsList();
        return true;
      } else {
        toast.error(response.message || "An unexpected error occurred");
        return false;
      }
    } catch (error) {
      console.error("Error.", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handlePupil = async () => {
    const isValid = await trigger("currentPupil");
    if (!isValid) return;

    const currentPupil = getValues("currentPupil");

    if (editingPupilIndex !== null) {
      updatePupil(editingPupilIndex, {
        ...currentPupil,
        pupilId: admissionInquiryDtos[editingPupilIndex].pupilId,
      });
      setEditingPupilIndex(null);
    } else {
      const newPupil = {
        ...currentPupil,
        pupilId: 0,
        rhfId: uuidv4(),
      };
      appendPupil(newPupil);

      setLastAddedIndex(admissionInquiryDtos.length);
    }

    // Reset the form for the next entry
    setValue("currentPupil", {
      id: 0,
      pupilId: 0,
      createdBy: localStorage.getItem("userId"),
      updatedBy: localStorage.getItem("userId"),
      familyCode: sharedData.code || "",
      code: "",
      centerSessionId: sessionsData.length > 0 ? sessionsData[0].id : "",
      termId: "",
      isAttended: "",
      firstName: "",
      middleName: "",
      lastName: "",
      arabicForeNames: "",
      arabicMiddleName: "",
      arabicSurName: "",
      greGorianDOB: "",
      hijriDOB: "",
      isSibilingEnrolled: "",
      nationality:
        nationalityData.length > 0
          ? nationalityData
              .find(
                (option) =>
                  option.value?.toLowerCase() === "saudi" ||
                  option.name?.toLowerCase() === "saudi"
              )
              ?.value.toString() || nationalityData[0].value.toString()
          : "",
      gender: "",
      iqamaNumber: "",
      iqamaExpiryDate: null,
      passportCountryId: null,
      passportExpiryDate: null,
      passportNumber: null,
      passportValidFrom: "",
      gccNumber: "",
      gccExpiryDate: null,
      saudiId: "",
      fluentInEnglish: "",
      fluentInArabic: "",
      homeLanguageId: "",
      userId: sharedData.userId,
      organizationId: organizationId,
      centerId: centerId,
      isSubmit: false,
      isCenterSubmit: false,
      centerUpdatedBy: "",
      assessmentSlotId: 0,
    });
  };

  const save = async () => {
    const currentPupil = getValues("currentPupil");

    const textFieldsToCheck = [
      "firstName",
      "lastName",
      "centerClassId",
      "termId",
      "isAttended",
      "arabicForeNames",
      "arabicSurName",
    ];

    const isTextInputFilled = textFieldsToCheck.some(
      (field) =>
        currentPupil[field] !== undefined &&
        currentPupil[field] !== null &&
        String(currentPupil[field]).trim() !== ""
    );

    if (isTextInputFilled) {
      const isValid = await trigger("currentPupil");
      if (!isValid) {
        return false;
      }

      await handlePupil();
    }

    const pupilData = getValues("admissionInquiryDtos");

    if (pupilData.length === 0) {
      toast.error("Please add at least one pupil before saving.");
      return false;
    }

    const payload = {
      admissionInquiryDtos: pupilData.map((pupil) => ({
        id: pupil.pupilId || 0,
        familyCode: sharedData.code || "",
        centerSessionId: pupil.centerSessionId || "",
        termId: pupil.termId || "",
        isAttended: pupil.isAttended === "Yes",
        firstName: pupil.firstName || "",
        middleName: pupil.middleName || "",
        lastName: pupil.lastName || "",
        arabicForeNames: pupil.arabicForeNames || "",
        arabicMiddleName: pupil.arabicMiddleName || "",
        arabicSurName: pupil.arabicSurName || "",
        greGorianDOB: pupil.greGorianDOB || "",
        hijriDOB: pupil.hijriDOB || "",
        isSibilingEnrolled: pupil.isSibilingEnrolled === "Yes",
        nationality: pupil.nationality || "",
        gender: pupil.gender,
        iqamaNumber: pupil.iqamaNumber || "",
        iqamaExpiryDate: pupil.iqamaExpiryDate || null,
        passportCountryId:
          pupil.nationality.toLowerCase() === "saudi" ||
          !pupil.passportCountryId
            ? null
            : pupil.passportCountryId,
        passportExpiryDate: pupil.passportExpiryDate || null,
        passportNumber:
          pupil.nationality.toLowerCase() === "saudi" || !pupil.passportNumber
            ? null
            : pupil.passportNumber,
        passportValidFrom: pupil.passportValidFrom || "",
        gccNumber: pupil.gccNumber || "",
        gccExpiryDate: pupil.gccExpiryDate || null,
        saudiId: pupil.saudiId || "",
        fluentInEnglish: pupil.fluentInEnglish || "",
        fluentInArabic: pupil.fluentInArabic || "",
        homeLanguageId: pupil.homeLanguageId || "",
        createdBy: pupil.createdBy || localStorage.getItem("userId"),
        updatedBy: pupil.updatedBy || localStorage.getItem("userId"),
        userId: sharedData.userId,
        organizationId: organizationId,
        centerId: centerId,
        code: pupil.code || "",
        isSubmit: pupil.isSubmit || false,
        isCenterSubmit: pupil.isCenterSubmit,
        centerSubmissionDate: pupil.centerSubmissionDate,
        centerUpdatedBy: pupil.centerUpdatedBy,
        assessmentSlotId: pupil.assessmentSlotId,
        assessmentDate: pupil.assessmentDate,
        communicationRemarks: pupil.communicationRemarks || "",
        isCommunicatedToGuardian: pupil.isCommunicatedToGuardian,
        centerClassId: pupil.centerClassId,
        isInterviewScheduled: pupil.isInterviewScheduled,
        isInterviewTaken: pupil.isInterviewTaken,
        interviewRemarks: pupil.interviewRemarks || "",
        interviewDate: pupil.interviewDate,
        isAssessmentAdded: pupil.isAssessmentAdded,
        isAssessmentMarksAdded: pupil.isAssessmentMarksAdded,
        isAccepted: pupil.isAccepted,
        acceptanceRemarks: pupil.acceptanceRemarks || "",
        hasObservation: pupil.hasObservation,
        observationRemarks: pupil.observationRemarks || "",
        shouldRetakeExam: pupil.shouldRetakeExam,
        retakeExamRemarks: pupil.retakeExamRemarks || "",
        registrationVoucherNo: pupil.registrationVoucherNo,
        isPaidRegistration: pupil.isPaidRegistration,
        admissionVoucherNo: pupil.admissionVoucherNo,
        isPaidAdmission: pupil.isPaidAdmission,
      })),
    };

    const success = await savePupils(payload);
    if (success) {
      setShowSuccessModal(true);
    }
    return success;
  };

  const canProceedToNextStep = async () => {
    const pupilData = getValues("admissionInquiryDtos") || [];

    if (!Array.isArray(pupilData) || pupilData.length < 1) {
      return false;
    }

    return true;
  };

  useImperativeHandle(ref, () => ({
    save,
    canProceedToNextStep,
    getValues,
  }));

  const handleCustomDropdownSetting = (dropdownName) => {
    setCustomDropdownName(dropdownName);
    setIsCustomDropdownValuesModalVisible(true);
  };

  const handleDeletePupil = (index) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this pupil?"
    );
    if (!confirmed) return;

    removePupil(index);
    setShowInfoModal(true);

    if (editingPupilIndex === index) {
      setValue("currentPupil", {
        id: 0,
        pupilId: 0,
        createdBy: localStorage.getItem("userId"),
        updatedBy: localStorage.getItem("userId"),
        familyCode: sharedData.code || "",
        code: "",
        centerSessionId: sessionsData.length > 0 ? sessionsData[0].id : "",
        termId: "",
        isAttended: "",
        firstName: "",
        middleName: "",
        lastName: "",
        arabicForeNames: "",
        arabicMiddleName: "",
        arabicSurName: "",
        greGorianDOB: "",
        hijriDOB: "",
        isSibilingEnrolled: "",
        nationality:
          nationalityData.length > 0
            ? nationalityData
                .find(
                  (option) =>
                    option.value?.toLowerCase() === "saudi" ||
                    option.name?.toLowerCase() === "saudi"
                )
                ?.value.toString() || nationalityData[0].value.toString()
            : "",
        gender: "",
        iqamaNumber: "",
        iqamaExpiryDate: null,
        passportCountryId: null,
        passportExpiryDate: null,
        passportNumber: null,
        passportValidFrom: "",
        gccNumber: "",
        gccExpiryDate: null,
        saudiId: "",
        fluentInEnglish: "",
        fluentInArabic: "",
        homeLanguageId: "",
        userId: sharedData.userId,
        organizationId: organizationId,
        centerId: centerId,
        isSubmit: false,
        isCenterSubmit: false,
        centerUpdatedBy: "",
        assessmentSlotId: 0,
      });

      setStates([]);
      setCities([]);
      setEditingPupilIndex(null);
    }

    if (editingPupilIndex > index) {
      setEditingPupilIndex((prev) => prev - 1);
    }
  };

  const handleModalAddAnother = () => {
    setShowSuccessModal(false);
  };

  const handleModalNavigateToDocuments = () => {
    setShowSuccessModal(false);
    moveToNextStep(3);
  };

  return (
    <div className="form-step">
      {/* <h4 className="fs-8 mb-3 text-secondary">Pupil Details</h4> */}
      {isLoading && (
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      )}

      {/* Added Pupils List */}
      {admissionInquiryDtos.length > 0 && (
        <div className="mb-4">
          <h6>Added Pupils</h6>
          <div className="list-group">
            {admissionInquiryDtos.map((pupil, index) => {
              const isDisabledForParent =
                roleId !== "3" ? pupil.isCenterSubmit : false;

              const isEditing = editingPupilIndex === index;
              const isLastAdded = lastAddedIndex === index;

              return (
                <div
                  key={pupil.rhfId}
                  className={`list-group-item d-flex justify-content-between align-items-center border-primary ${
                    isEditing ? "bg-primary-subtle" : ""
                  } ${isLastAdded ? "bg-primary-subtle" : ""}`}
                >
                  <div className="fs-9">
                    {pupil.firstName} {pupil.lastName}
                  </div>
                  <div className="d-flex gap-1 ms-auto">
                    <button
                      type="button"
                      className="btn py-1 px-2 btn-outline-primary"
                      onClick={() => {
                        const pupilToEdit = { ...pupil };

                        setValue("currentPupil", pupilToEdit);
                        setValue("currentPupil", {
                          ...pupilToEdit,
                          pupilId: pupilToEdit.pupilId,
                        });

                        setEditingPupilIndex(index);
                      }}
                      disabled={isDisabledForParent}
                    >
                      <i className="fa fa-edit"></i>
                    </button>
                    <button
                      type="button"
                      className="btn py-1 px-2 btn-outline-primary"
                      onClick={() => handleDeletePupil(index)}
                      disabled={isDisabledForParent}
                    >
                      <i className="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="row g-3">
        <div>
          <h5 className="mb-0 p-2 px-3 text-white bg-brand rounded-pill">
            Pupil Details
          </h5>
        </div>

        {/* Pupil Form */}
        <div className="col-12">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label form-label-new">
                Applying For <span className="text-danger fs-9">*</span>
              </label>
              <select
                {...register("currentPupil.centerSessionId", {
                  required: "This field is required.",
                })}
                className="form-select"
              >
                <option value="">Select</option>
                {sessionsData.map((item, index) => (
                  <option key={index} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              {errors.currentPupil?.centerSessionId && (
                <small className="text-danger">This field is required.</small>
              )}
            </div>
            <div className="col-md-3">
              <label className="form-label form-label-new">
                Year Group <span className="text-danger fs-9">*</span>
              </label>
              <select
                {...register("currentPupil.centerClassId", {
                  required: "This field is required.",
                })}
                className="form-select"
              >
                <option value="">Select</option>
                {classesData.map((item, index) => (
                  <option key={index} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              {errors.currentPupil?.centerClassId && (
                <small className="text-danger">This field is required.</small>
              )}
            </div>
            <div className="col-md-3">
              <label className="form-label form-label-new">
                Entry Term <span className="text-danger fs-9">*</span>
              </label>
              <select
                {...register("currentPupil.termId", {
                  required: "This field is required.",
                })}
                className="form-select"
              >
                <option value="">Select</option>
                {termsData.map((term, index) => (
                  <option key={index} value={term.id}>
                    {term.type}
                  </option>
                ))}
              </select>
              {errors.currentPupil?.termId && (
                <small className="text-danger">This field is required.</small>
              )}
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                Attended Tour? <span className="text-danger fs-9">*</span>
              </label>
              <select
                {...register("currentPupil.isAttended", {
                  required: "This field is required.",
                })}
                className="form-select"
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.currentPupil?.isAttended && (
                <small className="text-danger">This field is required.</small>
              )}
            </div>

            {/* English Names */}
            <div className="col-md-3">
              <label className="form-label form-label-new">
                First Name <span className="text-danger fs-9">*</span>
              </label>
              <input
                {...register("currentPupil.firstName", {
                  required: "This field is required.",
                })}
                className="form-control"
              />
              {errors.currentPupil?.firstName && (
                <small className="text-danger">This field is required.</small>
              )}
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">Middle Name</label>
              <input
                {...register("currentPupil.middleName")}
                className="form-control"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                Last Name <span className="text-danger fs-9">*</span>
              </label>
              <input
                {...register("currentPupil.lastName", {
                  required: "This field is required.",
                })}
                className="form-control"
              />
              {errors.currentPupil?.lastName && (
                <small className="text-danger">This field is required.</small>
              )}
            </div>

            {/* Arabic Names */}
            <div className="col-md-3">
              <label className="form-label form-label-new">
                Arabic Forenames <span className="text-danger fs-9">*</span>
              </label>
              <input
                {...register("currentPupil.arabicForeNames", {
                  required: "This field is required.",
                })}
                className="form-control"
              />
              {errors.currentPupil?.arabicForeNames && (
                <small className="text-danger">This field is required.</small>
              )}
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                Arabic Middle Name
              </label>
              <input
                {...register("currentPupil.arabicMiddleName")}
                className="form-control"
              />
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                Arabic Surname <span className="text-danger fs-9">*</span>
              </label>
              <input
                {...register("currentPupil.arabicSurName", {
                  required: "This field is required.",
                })}
                className="form-control"
              />
              {errors.currentPupil?.arabicSurName && (
                <small className="text-danger">This field is required.</small>
              )}
            </div>

            {/* Dates */}
            <div className="col-md-3">
              <label className="form-label form-label-new">
                Gregorian DOB <span className="text-danger fs-9">*</span>
              </label>
              <input
                type="date"
                {...register("currentPupil.greGorianDOB", {
                  required: "This field is required.",
                  validate: {
                    notFutureDate: (value) =>
                      (value && new Date(value) <= new Date()) ||
                      "Future dates are not allowed.",
                    notBefore2000: (value) =>
                      (value && new Date(value) >= new Date("2000-01-01")) ||
                      "Dates before the year 2000 are not allowed.",
                  },
                  // onChange: (e) => setGregorianDOB(e.target.value),
                })}
                className="form-control"
                min="2000-01-01"
                max={new Date().toISOString().split("T")[0]}
              />
              {errors.currentPupil?.greGorianDOB && (
                <small className="text-danger">This field is required.</small>
              )}
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                Hijri DOB <span className="text-danger fs-9">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                readOnly
                {...register("currentPupil.hijriDOB", {
                  required: "This field is required.",
                })}
              />
              {errors.currentPupil?.hijriDOB && (
                <small className="text-danger">This field is required.</small>
              )}
            </div>

            {/* Sibling Info */}
            <div className="col-md-3">
              <label className="form-label form-label-new">
                Sibling Enrolled? <span className="text-danger fs-9">*</span>
              </label>
              <select
                {...register("currentPupil.isSibilingEnrolled", {
                  required: "This field is required.",
                })}
                className="form-select"
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.currentPupil?.isSibilingEnrolled && (
                <small className="text-danger">This field is required.</small>
              )}
            </div>

            {/* Nationality & Gender */}
            <div className="col-md-3">
              <label className="form-label form-label-new">
                Nationality <span className="text-danger fs-9">*</span>
              </label>
              <div className="d-flex gap-2">
                <div className="w-100">
                  <select
                    {...register("currentPupil.nationality", {
                      required: "This field is required.",
                    })}
                    className="form-select"
                    onChange={(e) => {
                      const value = e.target.value;
                      setValue("currentPupil.nationality", value, {
                        shouldValidate: true,
                      });
                      if (value.toLowerCase() === "saudi") {
                        setValue("currentPupil.saudiId", "99999");
                        setValue("currentPupil.passportCountryId", null);
                        setValue("currentPupil.passportNumber", null);
                      } else {
                        setValue("currentPupil.saudiId", "");
                      }
                    }}
                  >
                    <option value="">Select</option>
                    {nationalityData.map((nationality, index) => (
                      <option key={index} value={nationality.value}>
                        {nationality.value}
                      </option>
                    ))}
                  </select>
                  {errors.currentPupil?.nationality && (
                    <small className="text-danger">
                      This field is required.
                    </small>
                  )}
                </div>
                {["9", "10", "11"].includes(roleId) ? null : (
                  <div>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      style={{
                        padding: "5.5px 10px",
                      }}
                      onClick={() => handleCustomDropdownSetting("nationality")}
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                Gender <span className="text-danger fs-9">*</span>
              </label>
              <select
                {...register("currentPupil.gender", {
                  required: true,
                })}
                className="form-select"
              >
                <option value="">Select</option>
                <option value="1">Male</option>
                <option value="0">Female</option>
              </select>
              {errors.currentPupil?.gender && (
                <small className="text-danger">This field is required.</small>
              )}
            </div>

            {/* Iqama Details */}
            <div className="col-md-3">
              <label className="form-label form-label-new">
                ID Number <span className="text-danger fs-9">*</span>
              </label>
              <input
                {...register("currentPupil.iqamaNumber", {
                  required: "This field is required.",
                  pattern: {
                    value: /^\d{10}$/,
                    message: "ID Number must be 10 digits.",
                  },
                })}
                className="form-control"
                maxLength={10}
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                }}
              />
              {errors.currentPupil?.iqamaNumber && (
                <small className="text-danger">
                  {errors.currentPupil.iqamaNumber.message}
                </small>
              )}
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                ID Expiry
                {/* <span className="text-danger fs-9">*</span> */}
              </label>
              <input
                type="date"
                {...register("currentPupil.iqamaExpiryDate", {
                  // required: "This field is required.",
                  // validate: {
                  //   notPastDate: (value) => {
                  //     const selectedDate = new Date(value);
                  //     const today = new Date();
                  //     today.setHours(0, 0, 0, 0);
                  //     const isEditing = !!getValues("currentPupil.pupilId");
                  //     if (isEditing) {
                  //       return true;
                  //     }
                  //     return (
                  //       selectedDate >= today || "Please select a valid date."
                  //     );
                  //   },
                  // },
                })}
                className="form-control"
                // min={new Date().toISOString().split("T")[0]}
              />
              {errors.currentPupil?.iqamaExpiryDate && (
                <small className="text-danger">
                  {errors.currentPupil.iqamaExpiryDate.message}
                </small>
              )}
            </div>

            {/* <div className="col-md-3">
              <label className="form-label form-label-new">Saudi ID</label>
              <input
                type="text"
                {...register("currentPupil.saudiId")}
                className="form-control"
              />
            </div> */}

            <div className="col-md-3">
              <label className="form-label form-label-new">
                Home Language <span className="text-danger fs-9">*</span>
              </label>
              <div className="d-flex gap-2">
                <div className="w-100">
                  <select
                    {...register("currentPupil.homeLanguageId", {
                      required: "This field is required.",
                    })}
                    className="form-select"
                  >
                    <option value="">Select</option>
                    {homeLanguageData.map((homeLang, index) => (
                      <option key={index} value={homeLang.id}>
                        {homeLang.value}
                      </option>
                    ))}
                  </select>
                  {errors.currentPupil?.homeLanguageId && (
                    <small className="text-danger">
                      This field is required.
                    </small>
                  )}
                </div>
                {["9", "10", "11"].includes(roleId) ? null : (
                  <div>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      style={{
                        padding: "5.5px 10px",
                      }}
                      onClick={() =>
                        handleCustomDropdownSetting("homeLanguage")
                      }
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                Fluent in English? <span className="text-danger fs-9">*</span>
              </label>
              <select
                {...register("currentPupil.fluentInEnglish", {
                  required: "This field is required.",
                })}
                className="form-select"
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.currentPupil?.fluentInEnglish && (
                <small className="text-danger">This field is required.</small>
              )}
            </div>

            <div className="col-md-3">
              <label className="form-label form-label-new">
                Fluent in Arabic? <span className="text-danger fs-9">*</span>
              </label>
              <select
                {...register("currentPupil.fluentInArabic", {
                  required: "This field is required.",
                })}
                className="form-select"
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.currentPupil?.fluentInArabic && (
                <small className="text-danger">This field is required.</small>
              )}
            </div>

            <div>
              <h5 className="mb-0 p-2 px-3 text-white bg-brand rounded-pill">
                Passport Details
              </h5>
            </div>

            {/* Pupil Form */}
            <div className="col-12">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label form-label-new">
                    Passport Country{" "}
                    {nationality.toLowerCase() !== "saudi" && (
                      <span className="text-danger fs-9">*</span>
                    )}
                  </label>
                  <select
                    {...register("currentPupil.passportCountryId", {
                      validate: (value) =>
                        nationality.toLowerCase() === "saudi" || value
                          ? true
                          : "This field is required.",
                    })}
                    className="form-select"
                  >
                    <option value="">Select</option>
                    {countries.map((country, index) => (
                      <option key={index} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  {errors.currentPupil?.passportCountryId && (
                    <small className="text-danger">
                      {errors.currentPupil.passportCountryId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-3">
                  <label className="form-label form-label-new">
                    Passport Number{" "}
                    {nationality.toLowerCase() !== "saudi" && (
                      <span className="text-danger fs-9">*</span>
                    )}
                  </label>
                  <input
                    {...register("currentPupil.passportNumber", {
                      validate: (value) =>
                        nationality.toLowerCase() === "saudi" || value
                          ? true
                          : "This field is required.",
                    })}
                    className="form-control"
                  />
                  {errors.currentPupil?.passportNumber && (
                    <small className="text-danger">
                      {errors.currentPupil.passportNumber.message}
                    </small>
                  )}
                </div>
                <div className="col-md-3">
                  <label className="form-label form-label-new">
                    Passport Expiry
                  </label>
                  <input
                    type="date"
                    {...register("currentPupil.passportExpiryDate")}
                    className="form-control"
                  />
                  {errors.currentPupil?.passportExpiryDate && (
                    <small className="text-danger">
                      {errors.currentPupil.passportExpiryDate.message}
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={async () => {
              await save();
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <span
                className="spinner-border spinner-custom-size me-1"
                aria-hidden="true"
              ></span>
            ) : null}
            Save Pupils
          </button>
        </div>
      </div>

      {isCustomDropdownValuesModalVisible &&
        createPortal(
          <DropdownWithCustomValuesModal
            dropdownName={customDropdownName}
            onClose={() => {
              setIsCustomDropdownValuesModalVisible(false);
              if (customDropdownName === "homeLanguage") {
                fetchCommonSettings("homeLanguage", setHomeLanguageData);
              } else if (customDropdownName === "nationality") {
                fetchCommonSettings("nationality", setNationalityData);
              }
            }}
          />,
          document.body // or a #modal-root div
        )}

      <StepSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onAddAnother={handleModalAddAnother}
        onNavigate={handleModalNavigateToDocuments}
        message="Pupils Saved Successfully!"
        nextButtonText="Go to Documents"
      />

      <RegStepItemDeleteInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        message="Pupil removed from the list. Click 'Save Pupils' to delete it permanently."
      />
    </div>
  );
});

export default Step2Pupils;
